/**
 * 运营弹窗 Tab（ADM-OPS-S007）。**恒渲染场景闭集全集三行，运营只做上架 / 下架**。
 *
 * 为什么不暴露「新建 / 删除」：`scenario` 是 DB CHECK 闭集（三个内置场景），运营创造不出第四个；
 * 后端的 POST 建的是「本层某场景的配置行」、DELETE 按契约 §5.4「只用于清理误建行」。把这两个
 * 动词摆到界面上，运营会误以为能造新场景 / 能用删除来下线弹窗。故建行降为开关的副作用：
 * 未建行的场景点「上架」= POST 建行（服务端定 is_active=false）+ PATCH 置 true，两步内部完成。
 *
 * ⚠️ **三态不是两态**：`未配置` 与 `已建行但下架` 对 C 端**不等价** —— 该 App 零行会按 §5.1
 * **回落全局层**（可能拿到全局层的上架场景），而建齐行并全部下架才是「该 App 一个都不出」。
 * 故状态列必须区分三态，且零行时给「建齐三行（下架）」的显式动作，不能只画两态开关。
 *
 * **无语言维度**（dev-docs `02-API契约/A2-运营配置.md` §5 头部）：弹窗没有多语文案，其上下架对一个
 * App 的**全部语言一致**，故本 Tab 不吃 `lang`、不显示语言、也不接 §0.4 语言复制 / §0.5 移除覆盖。
 * 早期口径曾是「App × 语言」（原型 `ops2.jsx` 的 `PS.list(scopeId, lang)` 仍是那一版），已由张培在
 * dev-docs#733 review（PR733-007）推翻 —— 原型待回流，差异登记见 `docs/plans/`。
 *
 * **两层单层选取**（§5.1）：全局层（`app_id IS NULL`）/ App 覆盖层，取一层不并集；该 App 零行时
 * 服务端返 `GLOBAL_INHERITED` + 空 `items`，此时表里就该是空的 —— C 端读的是全局层那份。
 *
 * 第二个 Section = **Paywall 落点**（ADM-OPS-S008，A2 §6.1）：本 App 各 active 场景共用的 Adapty placement，
 * **只读**。本页管弹窗用不用，落点（用哪种支付方式）的配置与清空在「支付管理 › 支付页面」的运营弹窗页
 * （A8 §7.5），本区给直达入口。落点只适用原生 App：WEB 不请求落点与 Adapty catalog，只渲染不适用说明。
 */

import type { OperationalPopupSceneResponse, PopupSceneEnvelopeItem } from '@novel-admin/shared';
import type { ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAppScope } from '~/app/app-scope-store';
import { isPaymentProviderChannel } from '~/data/adapters/acquiring-prerequisite';
import {
  paymentProviderConfigRoute,
  useAcquiringPrerequisiteStates,
} from '~/data/payment-provider-config-queries';
import { useAdaptyCatalog, useAppDetail, useApps } from '~/data/queries';
import {
  type OperationalPopupSceneMutations,
  useOperationalPopupScenes,
  usePopupPlacements,
} from '~/data/use-ops-shelf-popup';
import { Icons } from '~/lib/icons';
import { Section } from '~/ui/Section';
import { Switch } from '~/ui/Switch';
import { Table } from '~/ui/Table';
import { channelDisplayLabel } from '../apps/payment-method-labels';
import { isOpsGlobal } from './ops-scope';
import { POPUP_SCENARIO_OPTIONS } from './popup-scenarios';
import { errorToast } from './use-ops-mutations';

const FORBIDDEN_ROLE_TITLE = '权限不足 · 该操作受角色限制（后端 requireRole 为权威）';
const SCENE_NOTE: Record<string, string> = {
  payment_exit_retention: '用户在支付页返回时触发一次',
  first_purchase_offer: '未付费用户进入充值页触发',
  insufficient_balance_offer: '余额不足进入充值页触发',
  new_user_offer: '注册 72h 内首次打开客户端触发',
};

/** 二次确认弹窗配置（由 OpsPage 的 ConfirmDialog 承接，本 Tab 只提配置不自建壳）。 */
type PopupConfirm = (cfg: {
  title: string;
  message: string;
  tone?: 'danger' | 'warn' | 'default';
  confirmLabel?: string;
  requireTyping?: string;
  onConfirm: () => void;
}) => void;

export function OperationalPopupsTab({
  appId,
  mutations,
  canUpsert,
  onConfirm,
}: {
  appId: string;
  mutations: OperationalPopupSceneMutations;
  /** 场景上下架写档（root/admin，A2 §5.2~§5.4）*/
  canUpsert: boolean;
  onConfirm: PopupConfirm;
}) {
  const navigate = useNavigate();
  const { data: scenes, isLoading, isError } = useOperationalPopupScenes(appId);
  const isGlobalLayer = isOpsGlobal(appId);
  const byScenario = new Map(scenes.map((s) => [s.scenario, s]));
  /** 闭集全集恒三行；row 为 undefined = 本层未建该场景的配置行 */
  const rows = POPUP_SCENARIO_OPTIONS.map((o) => ({ ...o, row: byScenario.get(o.value) }));
  const layerEmpty = scenes.length === 0;
  const busy = mutations.create.isPending || mutations.update.isPending;

  // 已建行：PATCH 只翻 is_active（§5.3），不回传整行
  const toggleExisting = (s: PopupSceneEnvelopeItem) => {
    mutations.update.mutate(
      { id: s.id, body: { is_active: !s.is_active } },
      { onError: (err) => errorToast(err, s.is_active ? '下架失败' : '上架失败') },
    );
  };

  // 未建行上架：POST 建行（§5.2 服务端恒定 is_active=false）→ PATCH 置 true，与 FAQ §9.2 同款两步
  const createAndActivate = (scenario: string) => {
    mutations.create.mutate(
      // 全局层 app_id 传 null（§5.2「null = 写全局层」）；服务端取栅栏排他模式
      {
        app_id: isGlobalLayer ? null : appId,
        scenario: scenario as OperationalPopupSceneResponse['scenario'],
      },
      {
        onSuccess: (created) =>
          mutations.update.mutate(
            { id: created.id, body: { is_active: true } },
            { onError: (err) => errorToast(err, '已建行但上架失败 · 可在本行重试') },
          ),
        onError: (err) => errorToast(err, '上架失败'),
      },
    );
  };

  /** 零行时「建齐三行（下架）」——契约让某 App 全不出弹窗的唯一正确做法，不是删到零行 */
  const seedAllInactive = () => {
    for (const o of POPUP_SCENARIO_OPTIONS) {
      if (byScenario.has(o.value)) continue;
      mutations.create.mutate(
        { app_id: isGlobalLayer ? null : appId, scenario: o.value },
        { onError: (err) => errorToast(err, `建行失败（${o.label}）`) },
      );
    }
  };

  return (
    <div className="vstack" style={{ gap: 14 }}>
      <Section
        pad="4px 18px 16px"
        title={isGlobalLayer ? '运营弹窗 · 全局层' : '运营弹窗 · App 覆盖层'}
        sub={
          isGlobalLayer
            ? '场景取自闭集 · 无多语文案与语言维度，只管理上下架；不覆盖的 App 全读这一份'
            : '场景取自闭集 · 无多语文案与语言维度，只管理上下架；本层对该 App 全部语言一致'
        }
        action={
          // 只在本层零行时出现：契约让某 App「一个都不出」的正确做法是建齐行并全部下架，
          // 而不是删到零行（零行会回落全局层）。建完这个动作即消失，日常只剩开关。
          layerEmpty && !isLoading && !isError ? (
            <button
              type="button"
              className="btn btn-sm"
              disabled={!canUpsert || busy}
              title={
                !canUpsert
                  ? FORBIDDEN_ROLE_TITLE
                  : '建齐三个场景的配置行并全部保持下架 —— 本层从此不再回落全局层'
              }
              onClick={() =>
                onConfirm({
                  title: '建齐三行并全部下架？',
                  message:
                    '本层当前零行，C 端会读全局层。建齐后该 App 只读本层，三个场景均为下架（一个都不出弹窗）。此后可逐个上架。',
                  confirmLabel: '建齐',
                  onConfirm: seedAllInactive,
                })
              }
            >
              <Icons.IconPlus size={11} />
              建齐三行（下架）
            </button>
          ) : null
        }
      >
        <Table className="tbl">
          <thead>
            <tr>
              <th style={{ width: 240 }}>场景</th>
              <th>说明</th>
              <th style={{ width: 150 }}>上下架</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ value, label, row }) => {
              const active = row?.is_active === true;
              // 三态：未建行 / 已建行下架 / 已建行上架 —— 前两者对 C 端不等价，必须分开显示
              const stateLabel = !row ? '未配置' : active ? '上架' : '下架';
              const stateCls = !row ? 'chip-warn' : active ? 'chip-pos' : '';
              const lockedReason = !canUpsert
                ? FORBIDDEN_ROLE_TITLE
                : isLoading || isError
                  ? '场景状态未知 —— 正在读取或读取失败'
                  : undefined;
              return (
                <tr key={value}>
                  <td>
                    <div style={{ fontSize: 12, fontWeight: 500 }}>{label}</div>
                    <div className="mono" style={{ fontSize: 10.5, color: 'var(--fg-faint)' }}>
                      {value} · {row ? (row.app_id === null ? '全局层' : 'App 层') : '本层未建行'}
                    </div>
                  </td>
                  <td style={{ fontSize: 11.5, color: 'var(--fg-muted)' }}>
                    {SCENE_NOTE[value] || '—'}
                  </td>
                  <td>
                    <div className="hstack" style={{ gap: 8 }}>
                      <Switch
                        on={active}
                        size="sm"
                        disabled={!!lockedReason || busy}
                        onChange={() => {
                          if (lockedReason || busy) return;
                          if (row) {
                            onConfirm({
                              title: `${active ? '下架' : '上架'}「${label}」？`,
                              message: active
                                ? '下架后客户端在该触发点不再出此弹窗（对本层全部语言生效）。'
                                : '上架后该场景对本层全部语言生效。注意：服务端不校验落点 —— 下方「Paywall 落点」两个渠道都未配置时，客户端仍取不到 paywall、弹窗不会出；落点在「支付管理 › 支付页面 › 运营弹窗」配置。',
                              tone: active ? 'warn' : 'default',
                              confirmLabel: '确认',
                              onConfirm: () => toggleExisting(row),
                            });
                            return;
                          }
                          // 未建行 → 上架：先建行再置 true（§5.2 服务端恒定 false）
                          onConfirm({
                            title: `上架「${label}」？`,
                            message:
                              '本层尚未配置该场景，将先为它建配置行再上架。建行后该 App 只读本层、不再回落全局层。',
                            confirmLabel: '上架',
                            onConfirm: () => createAndActivate(value),
                          });
                        }}
                      />
                      <span
                        className={`chip ${stateCls}`}
                        style={{ fontSize: 10 }}
                        title={lockedReason}
                      >
                        {stateLabel}
                      </span>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
        <div
          className="hstack"
          style={{ gap: 6, marginTop: 10, fontSize: 11, color: 'var(--fg-muted)' }}
        >
          <Icons.IconLock size={11} style={{ color: 'var(--fg-faint)' }} />
          <span>
            <span className="mono">access_level</span> 不在弹窗行内配置 —— 全系统一份、所有 App
            共用（ADM-SYS-S003），维护入口：
          </span>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            style={{ height: 20, padding: '0 6px' }}
            onClick={() => navigate('/settings/regions?sub=popup-access-levels')}
          >
            系统设置 → 地区与语言 → 弹窗权限档
          </button>
        </div>
      </Section>

      <PopupPlacementSection appId={appId} />
    </div>
  );
}

/**
 * scope App 的平台（大写 canonical：IOS / ANDROID / WEB），与 PaywallPage 同口径：列表优先；列表成功
 * 但不含该 App 时按详情确认，不由缺席猜测平台。聚合视图或未确认时 `platform=null`。
 */
function useScopedAppPlatform(appId: string): { platform: string | null; isError: boolean } {
  const isGlobal = isOpsGlobal(appId);
  const appsQuery = useApps();
  const listed = isGlobal ? undefined : appsQuery.data.find((app) => app.id === appId);
  const detailQuery = useAppDetail(appId, !isGlobal && !listed && appsQuery.isSuccess);
  const platform = isGlobal ? undefined : (listed?.platform ?? detailQuery.data?.platform);
  return {
    platform: platform ? platform.toUpperCase() : null,
    isError: !isGlobal && !listed && (appsQuery.isError || detailQuery.isError),
  };
}

/** 落点区不可配置时的说明壳（聚合视图 / WEB / 平台未确认），沿用同一 Section 标题。 */
function PlacementNotice({
  sub,
  tone = 'muted',
  children,
}: {
  sub: string;
  tone?: 'muted' | 'neg';
  children: ReactNode;
}) {
  return (
    <Section pad="4px 18px 16px" title="Paywall 落点（Adapty placement）" sub={sub}>
      <div
        className="hstack"
        style={{
          gap: 8,
          padding: '14px 0 4px',
          fontSize: 11.5,
          color: tone === 'neg' ? 'var(--neg)' : 'var(--fg-muted)',
        }}
      >
        {tone === 'neg' ? (
          <Icons.IconAlert size={12} />
        ) : (
          <Icons.IconInfo size={12} style={{ color: 'var(--fg-faint)' }} />
        )}
        <span>{children}</span>
      </div>
    </Section>
  );
}

/**
 * Paywall 落点 Section（ADM-OPS-S008 / A2 §6.1）—— 本 App 运营弹窗共用的 Adapty placement，**只读**。
 *
 * 分工：运营弹窗页管弹窗用不用（上方场景上下架），支付页管用哪种支付方式。落点的写入 / 清空唯一入口是
 * 「支付管理 › 支付页面」里该 App 的运营弹窗页（A8 §7.5「operational_popup 分支」）。这里只展示当前落点，
 * 让运营上架场景前一眼看出支付有没有配好，并给直达入口。
 *
 * 按结算通道闭集逐行渲染（`SETTLEMENT_CHANNELS`，即 `app_payment_methods` 的 CHECK 闭集）：未建行 / 建了行但未填值
 * 都归「未配置」，与 C 端「不参与 serving」同口径。
 *
 * **无全局层**：落点行落在 `app_payment_methods`，`app_id` 非空 —— 聚合视图下无行可看，渲染指引而不是一张
 * 恒空的表（画空表会被读成「这个 App 没配」）。
 *
 * **仅原生 App**（A8 §8：只有 IOS / ANDROID 接入 Adapty，服务端对 WEB 拒绝）：平台确认为原生前不请求落点与
 * Adapty catalog；WEB 渲染不适用说明，也不把服务端拒绝误报成读取失败。
 */
function PopupPlacementSection({ appId }: { appId: string }) {
  const navigate = useNavigate();
  const setGlobalAppId = useAppScope((s) => s.setAppId);
  const scopedPlatform = useScopedAppPlatform(appId);
  const isNativeApp = scopedPlatform.platform === 'IOS' || scopedPlatform.platform === 'ANDROID';
  const { data: channels, isError, isSuccess, enabled } = usePopupPlacements(appId, isNativeApp);
  // 已配置的落点必须是 Adapty 上真实存在的 placement —— 拼错的 slug 写得进去，但 C 端按它拉 Paywall
  // 必然拉空、弹窗静默不出。判据取「真同步存在」（catalog 含该值），与支付页 placement 配置同源。
  const adaptyCatalog = useAdaptyCatalog(isNativeApp ? appId : undefined);
  const placementOptions = adaptyCatalog.data?.placements ?? [];
  // 框架通道（如 PayerMax）落点已填，但本 App 该通道配置未就绪时 C 端不下发（字典 65 §1）——「落点已配置」
  // 不代表弹窗能用它付款，在行内补一句；读不到配置时不下结论。hook 须在下方各早返回之前调用。
  const acquiringOf = useAcquiringPrerequisiteStates(appId, {
    isNativeApp: true,
    enabled: enabled && isNativeApp,
  });

  if (!enabled) {
    return (
      <PlacementNotice sub="落点按 App 配置，没有全局层">
        运营弹窗的 Paywall 落点存在 App 维度上（每个 App 各自一份），聚合视图下没有可展示的行 ——
        请在顶栏切到具体 App 再查看。
      </PlacementNotice>
    );
  }
  if (scopedPlatform.platform === 'WEB') {
    return (
      <PlacementNotice sub="仅原生 App（iOS / Android）适用">
        Web App 不接入 Adapty，运营弹窗的 Paywall 落点不适用于 Web；Web
        售卖使用自家商品目录（支付管理 → 商品）。
      </PlacementNotice>
    );
  }
  if (!isNativeApp) {
    // 平台未确认前不请求落点：不能先按原生 App 取数，也不能把空态当「未配置」上屏
    return scopedPlatform.isError ? (
      <PlacementNotice sub="落点按 App 配置，没有全局层" tone="neg">
        App 平台读取失败 —— 暂不能判断落点是否适用；请刷新后重试。
      </PlacementNotice>
    ) : (
      <PlacementNotice sub="落点按 App 配置，没有全局层">正在确认 App 平台…</PlacementNotice>
    );
  }

  // 取数未就绪时 channels 是「全未配置」的空态 fallback，**不代表真的没配** —— 状态列不下结论。
  // 判据取 !isSuccess：离线 / 请求挂起时 isLoading 与 isError 同为 false，用它们判会把空态当真值上屏。
  const statusUnknown = !isSuccess;

  // 支付页按全局 app-scope 取数，而本页的 App 范围是页内状态 —— 跳转前把全局 scope 对齐到当前 App，
  // 否则落到支付页看到的是另一个 App 的运营弹窗页。
  const goToPaywall = () => {
    setGlobalAppId(appId);
    navigate('/paywall?tab=pages&scenario=operational_popup');
  };

  return (
    <Section
      pad="4px 18px 16px"
      title="Paywall 落点（Adapty placement）"
      sub="本 App 全部上架中的运营弹窗场景共用同一落点 · 各渠道共用同一个 placement；未配置的渠道不参与下发 · 在「支付管理 › 支付页面」的运营弹窗页配置"
    >
      <Table className="tbl">
        <thead>
          <tr>
            <th style={{ width: 240 }}>支付渠道</th>
            <th>Adapty placement_id</th>
            <th style={{ width: 170 }}>状态</th>
          </tr>
        </thead>
        <tbody>
          {channels.map((ch) => (
            <tr key={`${appId}:${ch.paymentMethod}`}>
              <td>
                <div style={{ fontSize: 12, fontWeight: 500 }}>{ch.label}</div>
                <div className="mono" style={{ fontSize: 10.5, color: 'var(--fg-faint)' }}>
                  {ch.paymentMethod}
                </div>
              </td>
              <td>
                {ch.configured ? (
                  <span className="mono" style={{ fontSize: 12 }}>
                    {ch.placementId}
                  </span>
                ) : (
                  <span style={{ fontSize: 11.5, color: 'var(--fg-faint)' }}>
                    {statusUnknown ? '—' : '未配置 · 不参与运营弹窗下发'}
                  </span>
                )}
                {/* 已配置但不在 Adapty 同步列表：值写进去了但客户端按它拉不到 Paywall，必须显眼提示。
                    仅在 catalog 确已同步（非空）时下这个断言 —— 空 catalog 时无从判断，不误报。 */}
                {ch.configured &&
                  placementOptions.length > 0 &&
                  !placementOptions.some((o) => o.placement_id === ch.placementId) && (
                    <div style={{ fontSize: 10.5, color: 'var(--warn)', marginTop: 4 }}>
                      ⚠ 该 placement 不在 Adapty 同步列表中 —— 客户端按它拉不到 Paywall，弹窗不会出
                    </div>
                  )}
                {ch.configured &&
                  isPaymentProviderChannel(ch.paymentMethod) &&
                  acquiringOf(ch.paymentMethod).problem !== null && (
                    <div style={{ fontSize: 10.5, color: 'var(--warn)', marginTop: 4 }}>
                      ⚠ 本 App 的 {ch.label} 配置{acquiringOf(ch.paymentMethod).problem} —— C
                      端不下发该通道，弹窗里拿不到它的支付方式。
                      <Link
                        to={paymentProviderConfigRoute(ch.paymentMethod)}
                        style={{ marginLeft: 4 }}
                      >
                        去 {channelDisplayLabel(ch.paymentMethod)} 配置
                      </Link>
                    </div>
                  )}
              </td>
              <td>
                {statusUnknown ? (
                  <span className="chip" style={{ fontSize: 10 }}>
                    状态未知
                  </span>
                ) : (
                  <span
                    className={`chip ${ch.configured ? 'chip-pos' : 'chip-warn'}`}
                    style={{ fontSize: 10 }}
                  >
                    {ch.configured ? '落点已配置' : '落点未配置'}
                  </span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
      {isError && (
        <div
          className="hstack"
          style={{ gap: 6, marginTop: 10, fontSize: 11, color: 'var(--neg)' }}
        >
          <Icons.IconAlert size={11} />
          <span>落点读取失败 —— 当前显示的不是真实配置；请刷新后重试。</span>
        </div>
      )}
      <div
        className="hstack"
        style={{ gap: 6, marginTop: 10, fontSize: 11, color: 'var(--fg-muted)' }}
      >
        <Icons.IconInfo size={11} style={{ color: 'var(--fg-faint)' }} />
        <span>运营弹窗管弹窗用不用，支付页管用哪种支付方式 —— 落点的配置与清空在支付页完成：</span>
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          style={{ height: 20, padding: '0 6px' }}
          onClick={goToPaywall}
        >
          支付管理 → 支付页面 → 运营弹窗
        </button>
      </div>
    </Section>
  );
}
